'use client';

import * as React from 'react';
import { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@/components/auth/AuthProvider';
import { InfopankMonitoredCompanies } from '@/apiTypes/types';
import { IInfopankApiReqResolvedProps } from '@/lib/interfaces/infopankApiReq.interface';
import { useTRPC } from '@/trpc/client';
import { monitoredCompaniesQueryOptions } from '@/lib/queries/accountQueries';
import { subscribeMutationOptions, subscriptionStatusQueryOptions, unsubscribeMutationOptions } from '@/lib/queries/monitoringQueries';

interface ISubscriptionsContext {
  subscriptions: InfopankMonitoredCompanies[];
  subscriptionsError: unknown;
  subscriptionsPending: boolean;
  add: (id: string, name?: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  subscriptionStatus: (id: string) => Promise<{ subscribed: boolean; couldSubscribe: boolean }>;
}

export const SubscriptionsContext = createContext<ISubscriptionsContext>({
  subscriptions: [],
  subscriptionsError: null,
  subscriptionsPending: false,
  add: () => Promise.resolve(),
  remove: () => Promise.resolve(),
  subscriptionStatus: () =>
    Promise.resolve({
      subscribed: false,
      couldSubscribe: false,
    }),
});

const SubscriptionsProvider: React.FC<PropsWithChildren & IInfopankApiReqResolvedProps> = ({ children, params }) => {
  const { user, orderCode, orderInitialized } = useContext(AuthContext);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const locale = params.lang;
  const hasSubscription = orderInitialized && !!user && !!orderCode;

  const subscriptionsQuery = useQuery(monitoredCompaniesQueryOptions({ trpc, locale, enabled: hasSubscription }));
  const subscriptions = subscriptionsQuery.data ?? [];

  const subscribeMutation = useMutation(subscribeMutationOptions({ trpc, queryClient }));
  const unsubscribeMutation = useMutation(unsubscribeMutationOptions({ trpc, queryClient }));

  const add = async (id: string, name?: string) => {
    if (!user) {
      return;
    }
    await subscribeMutation.mutateAsync({ externalId: id, name, locale });
  };

  const remove = async (id: string) => {
    if (!user) {
      return;
    }
    await unsubscribeMutation.mutateAsync({ externalId: id, locale });
  };

  const getCompanySubscriptionStatus = useCallback(
    async (id: string) => {
      const initialSubscriptionStatus = {
        subscribed: false,
        couldSubscribe: false,
      };
      if (!hasSubscription) {
        return initialSubscriptionStatus;
      }

      const data = await queryClient.fetchQuery(subscriptionStatusQueryOptions({ trpc, externalId: id }));

      if (!data) {
        return initialSubscriptionStatus;
      }

      return {
        subscribed: data.subscribed,
        couldSubscribe: data.couldSubscribe,
      };
    },
    [queryClient, trpc, hasSubscription],
  );

  return (
    <SubscriptionsContext.Provider
      value={{
        add,
        remove,
        subscriptionStatus: getCompanySubscriptionStatus,
        subscriptions,
        subscriptionsError: subscriptionsQuery.error,
        subscriptionsPending: subscriptionsQuery.isLoading,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

export default SubscriptionsProvider;
